import { useState } from 'react';

import { TypeaheadSelectInputOption } from '@/components/form/baseInputs/inputTypes';
import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { useReportError } from '@/hooks/useReportError';
import { GiftingProposalGiftRecipientKind } from '@/types/schema';
import { getNodes } from '@/utils/graphqlUtils';

import { useGetScenarioGiftRecipientOptionsQuery } from './graphql/GetScenarioGiftRecipientOptions.generated';

const errorMessage =
  "We weren't able to initialize this form. Please refresh the page and try again.";

type GiftRecipientOptions = Array<
  TypeaheadSelectInputOption<string> & {
    kind: GiftingProposalGiftRecipientKind;
  }
>;

export function useScenarioGiftRecipientOptions(proposalId: string) {
  const { createErrorFeedback, showFeedback } = useFeedback();
  const { reportError } = useReportError();

  const [recipientOptions, setRecipientOptions] =
    useState<GiftRecipientOptions>([]);

  const queryMethods = useGetScenarioGiftRecipientOptionsQuery({
    variables: {
      where: {
        id: proposalId,
      },
    },
    onCompleted(data) {
      const proposals = getNodes(data.proposals);
      const portfolioBeneficiaries =
        proposals[0]?.giftingProposal?.outOfEstatePortfolios ?? null;
      const clientBeneficiaries = proposals[0]?.household?.clientProfiles ?? [];
      const organizationBeneficiaries =
        proposals[0]?.household?.clientOrganizations ?? [];

      const portfolioRecipients =
        portfolioBeneficiaries?.map((portfolio) => ({
          display: portfolio.displayName,
          value: portfolio.id,
          kind: GiftingProposalGiftRecipientKind.OutOfEstatePortfolio,
          groupName: 'Out-of-estate portfolios',
        })) ?? [];

      const clientRecipients = clientBeneficiaries.map((c) => ({
        display: c.displayName,
        value: c.id,
        kind: GiftingProposalGiftRecipientKind.Individual,
        groupName: 'Individuals',
      }));

      const organizationRecipients = organizationBeneficiaries.map((c) => ({
        display: c.name,
        value: c.id,
        kind: GiftingProposalGiftRecipientKind.Organization,
        groupName: 'Organizations',
      }));

      if (!portfolioBeneficiaries) {
        const errorDescription =
          'Expected outOfEstatePortfolios for the proposal.';
        const err = new Error(errorDescription);
        reportError(errorDescription, err, {
          proposalId,
        });
        showFeedback(errorMessage);
        throw err;
      }

      const selectOptions = [
        ...clientRecipients.sort((a, b) => a.display.localeCompare(b.display)),
        ...portfolioRecipients.sort((a, b) =>
          a.display.localeCompare(b.display)
        ),
        ...organizationRecipients.sort((a, b) =>
          a.display.localeCompare(b.display)
        ),
      ];
      setRecipientOptions(selectOptions);
    },
    onError: createErrorFeedback(
      "We weren't able to initialize this form. Please refresh the page and try again."
    ),
  });

  return { recipientOptions, ...queryMethods };
}
